/**
 * Live queue updates for a branch, with polling only as a fallback.
 *
 * Every screen that shows queue state has a Pusher subscription already, so
 * polling on top of it just duplicates work the socket has already done - on a
 * wall display that was 720 requests an hour against the queue-list endpoint
 * (which also runs the auto-expire sweep) for updates it had already received.
 *
 * So the poll runs ONLY while the socket is down:
 * - connected      -> events drive the UI, plus a slow heartbeat (below)
 * - disconnected   -> `onRefresh` every `pollMs` so a dead socket still updates
 * - on reconnect   -> one catch-up `onRefresh` for whatever the gap missed
 * - on foreground  -> one `onRefresh`, since a backgrounded tab is throttled
 *
 * The heartbeat covers the one failure the connection state cannot see: a
 * socket that connects fine but never delivers events (wrong Pusher app or
 * cluster, broadcasting misconfigured server-side). Without it an unattended
 * wall display would sit frozen indefinitely and look perfectly healthy. At
 * five minutes it costs 12 requests an hour against the 720 this replaces.
 *
 * Branch filtering happens here: the `queues` channel is global, so every
 * branch receives every other branch's events and each listener has to check.
 */
import { useEffect, useRef } from "react";
import {
    createPusherInstance,
    subscribeToQueueUpdates,
} from "../config/pusher";

// Only ever used while the socket is down, so it can be tighter than the old
// steady-state intervals without costing anything in the normal case.
export const FALLBACK_POLL_MS = 15000;

// Runs regardless of connection state; see the note above.
export const HEARTBEAT_MS = 300000;

const useQueueUpdates = ({
    branchId,
    onQueueEvent,
    onRefresh,
    enabled = true,
    pollMs = FALLBACK_POLL_MS,
    heartbeatMs = HEARTBEAT_MS,
}) => {
    const connected = useRef(false);
    // Held in refs so a caller re-creating its handlers each render never tears
    // down the socket or restarts the poll.
    const eventRef = useRef(onQueueEvent);
    const refreshRef = useRef(onRefresh);

    useEffect(() => {
        eventRef.current = onQueueEvent;
    }, [onQueueEvent]);

    useEffect(() => {
        refreshRef.current = onRefresh;
    }, [onRefresh]);

    useEffect(() => {
        if (!enabled || !branchId) return undefined;

        const pusher = createPusherInstance();
        if (!pusher) return undefined;

        pusher.connection.bind("state_change", ({ current }) => {
            const wasConnected = connected.current;
            connected.current = current === "connected";
            // Catch up on whatever the dropped socket missed.
            if (!wasConnected && connected.current) {
                refreshRef.current?.();
            }
        });

        const channel = subscribeToQueueUpdates(pusher, (data) => {
            const queueData = data?.queue || data?.data?.queue || data;
            const action = data?.action || data?.data?.action;

            // An event with no queue payload tells us something changed but not
            // what - fall back to a refetch.
            if (!queueData) {
                refreshRef.current?.();
                return;
            }

            // Queue numbers restart per branch, so an unfiltered event would
            // show another shop's ticket on this screen.
            const eventBranchId =
                queueData.QueueBranchID || queueData.branch_id;
            if (
                eventBranchId &&
                eventBranchId.toString() !== branchId.toString()
            ) {
                return;
            }

            eventRef.current?.(queueData, action, data);
        });

        return () => {
            connected.current = false;
            pusher.connection.unbind("state_change");
            channel.unbind("queue.updated");
            pusher.unsubscribe("queues");
            pusher.disconnect();
        };
    }, [branchId, enabled]);

    useEffect(() => {
        if (!enabled || !branchId) return undefined;

        const interval = setInterval(() => {
            if (!connected.current) refreshRef.current?.();
        }, pollMs);

        const onForeground = () => {
            if (document.visibilityState === "visible") refreshRef.current?.();
        };
        document.addEventListener("visibilitychange", onForeground);
        window.addEventListener("focus", onForeground);

        return () => {
            clearInterval(interval);
            document.removeEventListener("visibilitychange", onForeground);
            window.removeEventListener("focus", onForeground);
        };
    }, [branchId, enabled, pollMs]);

    useEffect(() => {
        if (!enabled || !branchId || !heartbeatMs) return undefined;

        const heartbeat = setInterval(() => refreshRef.current?.(), heartbeatMs);
        return () => clearInterval(heartbeat);
    }, [branchId, enabled, heartbeatMs]);

    return connected;
};

export default useQueueUpdates;
